// Shared display formatters.
//
// These were copy-pasted across pages — timeAgo in four files, fmtNumber in
// five, compactNum in four — and the copies had quietly drifted apart. Merging
// them is only safe where the implementations are actually identical, so this
// module holds the shared ones and the drifted variants stay where they are,
// named for what makes them different.
//
// The rule applied here: a merged function is faithful to the copies it
// replaces. Where copies differed ONLY in null-tolerance, the tolerant one wins
// — it is a strict superset, so no non-null input changes.
//
// Deliberately NOT merged, because their output genuinely differs:
//   Messages.tsx           timeAgo   compact chat format ("5m", not "5m ago")
//   SampleRequests.tsx     timeAgo   "never" for null, no week tier
//   CampaignPerformance    fmtNumber rounds thousands (1500 -> "2K", not "1.5K")
//   ActiveCampaigns.tsx    fmtDate   no year
//   fmtShortDate           two variants, one year-aware
//   fmtGmv                 five different signatures; see lib/pricing.ts

const EM_DASH = '—'

/** Parse a number that may arrive as a numeric string. Null for anything else. */
export function toNum(v: number | string | null | undefined): number | null {
  if (v == null) return null
  const n = typeof v === 'string' ? parseFloat(v) : v
  return Number.isFinite(n) ? n : null
}

/** 1.2M / 3.4K / 999 — plain, no grouping on the small case. */
export function compactNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return n.toString()
}

/** 1.2M / 3.4K / 1,234 — grouped on the small case, 0 for null. */
export function fmtNumber(n: number | undefined | null): string {
  if (n == null) return '0'
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return n.toLocaleString()
}

/** Views: like fmtNumber, but an em dash for zero/absent rather than "0". */
export function fmtViews(n: number): string {
  if (!n) return EM_DASH
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return n.toLocaleString()
}

/** Revenue in whole dollars: $1.2M / $3.4K / $1,234, em dash for zero. */
export function fmtRevenue(n: number): string {
  if (!n) return EM_DASH
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`
  return `$${Math.round(n).toLocaleString()}`
}

/** Money held in CENTS: $1.2M / $3.4K / $1,234. */
export function fmtCurrencyCents(cents: number): string {
  if (cents >= 100_000_000) return `$${(cents / 100_000_000).toFixed(1)}M`
  if (cents >= 100_000) return `$${(cents / 100_000).toFixed(1)}K`
  return `$${Math.round(cents / 100).toLocaleString()}`
}

/** Media duration as m:ss. */
export function fmtTime(s: number): string {
  if (!isFinite(s) || s < 0) return '0:00'
  const m = Math.floor(s / 60)
  const sec = Math.floor(s % 60)
  return `${m}:${sec.toString().padStart(2, '0')}`
}

/** "Mar 4, 2026". Em dash for null. */
export function fmtDate(d: string | null | undefined): string {
  if (!d) return EM_DASH
  try {
    return new Date(d).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  } catch {
    return d
  }
}

/** Whole days elapsed, e.g. "12d ago". Null when there is no date. */
export function fmtDaysAgo(date: string): string | null {
  if (!date) return null
  try {
    const days = Math.floor((Date.now() - new Date(date).getTime()) / 86400000)
    return `${days}d ago`
  } catch {
    return null
  }
}

/**
 * "just now" / "5m ago" / "3h ago" / "6d ago" / "4w ago", falling back to an
 * absolute date past ~2 months.
 *
 * Note the two tiers that make this different from the shorter copies: days up
 * to a fortnight, then weeks up to two months, then a date.
 */
export function timeAgo(iso: string): string {
  if (!iso) return ''
  const t = new Date(iso).getTime()
  if (isNaN(t)) return ''
  const diffSec = (Date.now() - t) / 1000
  if (diffSec < 60) return 'just now'
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`
  if (diffSec < 86400 * 14) return `${Math.floor(diffSec / 86400)}d ago`
  if (diffSec < 86400 * 60) return `${Math.floor(diffSec / 86400 / 7)}w ago`
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
